import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Breadcrumbs,
  Link,
  alpha,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { COLORS, FONT_SIZES, FONT_WEIGHTS } from '../../theme/constants';

/**
 * Same as PageLayout but header uses CSS grid so long pageDesc wraps
 * and action buttons stay in view (no horizontal scroll). Use for pages
 * with long descriptions (e.g. Standardized Fields list).
 */
const PageLayoutWithGrid = ({
  pageHeader,
  pageDesc,
  breadcrumbs,
  showBackButton,
  handleBackButtonClick,
  showPrimaryActionButton,
  handleActionClick,
  actionButtonText,
  actionButtonIcon,
  secondaryActions,
  footerContent,
  children,
  headerLogo,
  headerLogoFallback,
  maxWidth = '100%',
  headerIcon: HeaderIcon,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        bgcolor: 'background.default',
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          px: 4,
          py: 2.5,
          boxShadow: 1,
        }}
      >
        <Box
          sx={{
            maxWidth: maxWidth,
            mx: 'auto',
          }}
        >
          {/* Breadcrumbs */}
          {breadcrumbs && breadcrumbs.length > 0 && (
            <Breadcrumbs
              separator={<ChevronRightIcon sx={{ fontSize: 16 }} />}
              sx={{
                mb: 2,
                '& .MuiBreadcrumbs-separator': {
                  color: COLORS.text.hint,
                },
              }}
            >
              {breadcrumbs.map((crumb, index) => (
                <Link
                  key={index}
                  underline="hover"
                  color={index === breadcrumbs.length - 1 ? 'text.primary' : 'text.secondary'}
                  href={crumb.href}
                  onClick={crumb.onClick}
                  sx={{
                    fontSize: FONT_SIZES.body2,
                    fontWeight: index === breadcrumbs.length - 1 ? 500 : 400,
                    cursor: crumb.href || crumb.onClick ? 'pointer' : 'default',
                    transition: 'color 0.2s ease',
                    '&:hover': {
                      color: COLORS.primary.main,
                    },
                  }}
                >
                  {crumb.label}
                </Link>
              ))}
            </Breadcrumbs>
          )}

          {/* Main Header Row - grid so title/desc wrap and buttons stay, no horizontal scroll */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              alignItems: 'center',
              gap: 3,
              minWidth: 0,
            }}
          >
            {/* Left - Back button, Logo, Title (minWidth 0 so text can wrap) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2.5,
                minWidth: 0,
                pr: 3,
              }}
            >
              {showBackButton && (
                <IconButton
                  onClick={handleBackButtonClick}
                  sx={{
                    color: COLORS.text.secondary,
                    border: `1px solid ${COLORS.border.light}`,
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: COLORS.background.subtle,
                      borderColor: COLORS.border.main,
                    },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}

              {(headerLogo || headerLogoFallback) && (
                <Box
                  component="img"
                  src={headerLogo || headerLogoFallback}
                  alt="Logo"
                  onError={e => {
                    if (headerLogoFallback && e.currentTarget.src !== headerLogoFallback) {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = headerLogoFallback;
                    }
                  }}
                  sx={{
                    height: 48,
                    objectFit: 'contain',
                  }}
                />
              )}

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  minWidth: 0,
                  flex: 1,
                }}
              >
                {HeaderIcon && (
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      flexShrink: 0,
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${COLORS.primary.main}, ${alpha(COLORS.primary.main, 0.7)})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 4px 16px ${alpha(COLORS.primary.main, 0.3)}`,
                    }}
                  >
                    <HeaderIcon sx={{ fontSize: 28, color: COLORS.primary.contrastText }} />
                  </Box>
                )}
                <Box sx={{ minWidth: 0, flex: 1, py: 0.5 }}>
                  <Typography
                    sx={{
                      color: COLORS.text.primary,
                      fontSize: '1.75rem',
                      fontWeight: FONT_WEIGHTS.bold,
                      lineHeight: 1.2,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {pageHeader}
                  </Typography>
                  {pageDesc && (
                    <Typography
                      sx={{
                        color: COLORS.text.secondary,
                        fontSize: FONT_SIZES.body1,
                        mt: 0.5,
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                      }}
                    >
                      {pageDesc}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Right - Action Buttons (auto width, no shrink) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexShrink: 0,
              }}
            >
              {secondaryActions}

              {showPrimaryActionButton && (
                <Button
                  variant="contained"
                  startIcon={actionButtonIcon || <AddIcon />}
                  onClick={handleActionClick}
                  sx={{
                    background: `linear-gradient(135deg, ${COLORS.primary.main} 0%, ${COLORS.primary.dark} 100%)`,
                    textTransform: 'none',
                    fontWeight: FONT_WEIGHTS.semiBold,
                    fontSize: FONT_SIZES.button,
                    px: 3,
                    py: 1.25,
                    borderRadius: 2,
                    boxShadow: `0 4px 16px ${alpha(COLORS.primary.main, 0.35)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${COLORS.primary.dark} 0%, ${COLORS.primary.main} 100%)`,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 20px ${alpha(COLORS.primary.main, 0.45)}`,
                    },
                  }}
                >
                  {actionButtonText}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Content Section */}
      <Box
        sx={{
          flex: 1,
          p: 3,
          maxWidth: maxWidth,
          mx: 'auto',
          width: '100%',
        }}
      >
        {children}
      </Box>

      {/* Footer Section */}
      {footerContent && (
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            px: 4,
            py: 2,
          }}
        >
          <Box
            sx={{
              maxWidth: maxWidth,
              mx: 'auto',
            }}
          >
            {footerContent}
          </Box>
        </Box>
      )}
    </Box>
  );
};

PageLayoutWithGrid.propTypes = {
  pageHeader: PropTypes.string.isRequired,
  pageDesc: PropTypes.string,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      href: PropTypes.string,
      onClick: PropTypes.func,
    })
  ),
  showBackButton: PropTypes.bool,
  handleBackButtonClick: PropTypes.func,
  showPrimaryActionButton: PropTypes.bool,
  handleActionClick: PropTypes.func,
  actionButtonText: PropTypes.string,
  actionButtonIcon: PropTypes.node,
  secondaryActions: PropTypes.node,
  footerContent: PropTypes.node,
  children: PropTypes.node,
  headerLogo: PropTypes.string,
  headerLogoFallback: PropTypes.string,
  headerIcon: PropTypes.elementType,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

PageLayoutWithGrid.defaultProps = {
  showBackButton: false,
  handleBackButtonClick: () => {},
  showPrimaryActionButton: false,
  handleActionClick: () => {},
  actionButtonText: 'Add New',
};

export default PageLayoutWithGrid;
